import { useCallback, useEffect, useRef, useState } from "react";
import { Bold, Italic, Underline, Link as LinkIcon, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Source Note template editor — a plain textarea plus a small inline
 * formatting toolbar and a live server-rendered preview.
 *
 * Deliberately NOT a WYSIWYG. The template is a plain string containing
 * exact-match placeholder tokens (`{source_name}`, `{property_name}`),
 * and a rich-text editor would let an editor apply a mark to half a
 * token — producing `{source<strong>_name</strong>}`, which silently
 * never matches and ships literal braces to WordPress. The toolbar
 * below writes the tags for them and snaps any selection outward to
 * whole tokens, so that failure mode can't happen.
 *
 * The allowlist (<b> <strong> <i> <em> <u> <br> <a href>) is enforced
 * server-side in source_note.sanitize_template_html. Nothing here is a
 * security control — the preview is rendered by the same server helper
 * so what you see is exactly what gets published.
 */

// Placeholder tokens are `{lower_snake}` — see source_note.py.
const PLACEHOLDER_RE = /\{[a-zA-Z_]+\}/g;

/** Byte ranges of every `{token}` in `text`. */
export function tokenRanges(text) {
  const out = [];
  const re = new RegExp(PLACEHOLDER_RE.source, "g");
  let m;
  while ((m = re.exec(text)) !== null) {
    out.push([m.index, m.index + m[0].length]);
  }
  return out;
}

/**
 * Expand a selection so it never cuts a `{token}` in half. A collapsed
 * caret sitting inside a token is pushed to the token's end instead —
 * inserting tags mid-token is the one edit that silently breaks the
 * template.
 */
export function snapSelection(text, start, end) {
  let s = start;
  let e = end;
  for (const [ts, te] of tokenRanges(text)) {
    if (s === e) {
      if (s > ts && s < te) {
        s = te;
        e = te;
      }
      continue;
    }
    if (s > ts && s < te) s = ts;
    if (e > ts && e < te) e = te;
  }
  return [s, e];
}

/**
 * Wrap the current selection in `open`/`close`, or unwrap it if it is
 * already wrapped (so the buttons toggle). Returns the new text plus
 * the selection to restore.
 */
export function applyWrap(text, start, end, open, close) {
  const src = text || "";
  const [s, e] = snapSelection(src, start, end);
  const before = src.slice(0, s);
  const sel = src.slice(s, e);
  const after = src.slice(e);

  // Already wrapped, tags sitting just outside the selection.
  if (before.endsWith(open) && after.startsWith(close)) {
    const nb = before.slice(0, before.length - open.length);
    const na = after.slice(close.length);
    return {
      text: nb + sel + na,
      start: nb.length,
      end: nb.length + sel.length,
    };
  }
  // Already wrapped, tags included in the selection.
  if (
    sel.startsWith(open) &&
    sel.endsWith(close) &&
    sel.length >= open.length + close.length
  ) {
    const inner = sel.slice(open.length, sel.length - close.length);
    return {
      text: before + inner + after,
      start: before.length,
      end: before.length + inner.length,
    };
  }
  // Nothing selected — drop an empty pair and park the caret inside.
  if (s === e) {
    const caret = before.length + open.length;
    return { text: before + open + close + after, start: caret, end: caret };
  }
  return {
    text: before + open + sel + close + after,
    start: before.length + open.length,
    end: before.length + open.length + sel.length,
  };
}

/** Replace the selection with a placeholder token. */
export function insertToken(text, start, end, token) {
  const src = text || "";
  const [s, e] = snapSelection(src, start, end);
  const caret = s + token.length;
  return { text: src.slice(0, s) + token + src.slice(e), start: caret, end: caret };
}

/**
 * Coerce editor input into an http(s) URL. Returns "" for anything with
 * a different scheme (javascript:, data:) — the server allowlist would
 * reject those anyway, this just fails loudly at the point of entry.
 */
export function normalizeUrl(raw) {
  const v = (raw || "").trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return "";
  return `https://${v}`;
}

function ToolButton({ onClick, title, testId, children }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      title={title}
      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-[var(--border)] hover:border-[var(--ink)] hover:bg-white"
    >
      {children}
    </button>
  );
}

export default function SourceNoteTemplateField({
  value,
  onChange,
  defaultTemplate = "",
  testId = "source-note-template-input",
}) {
  const taRef = useRef(null);
  const [preview, setPreview] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState("");

  // Apply an edit and restore the caret/selection afterwards. The
  // textarea is controlled, so the DOM value only updates on the next
  // paint — hence rAF before setSelectionRange.
  const commit = useCallback(
    (result) => {
      onChange(result.text);
      requestAnimationFrame(() => {
        const el = taRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(result.start, result.end);
      });
    },
    [onChange],
  );

  const wrap = useCallback(
    (open, close) => {
      const el = taRef.current;
      if (!el) return;
      commit(applyWrap(value || "", el.selectionStart, el.selectionEnd, open, close));
    },
    [commit, value],
  );

  const addToken = useCallback(
    (token) => {
      const el = taRef.current;
      if (!el) return;
      commit(insertToken(value || "", el.selectionStart, el.selectionEnd, token));
    },
    [commit, value],
  );

  const addLink = useCallback(() => {
    const el = taRef.current;
    if (!el) return;
    const url = normalizeUrl(window.prompt("Link URL (https://…):", "https://"));
    if (!url) return;
    commit(
      applyWrap(
        value || "",
        el.selectionStart,
        el.selectionEnd,
        `<a href="${url}">`,
        "</a>",
      ),
    );
  }, [commit, value]);

  // Debounced server-rendered preview. A request counter guards against
  // out-of-order responses when the editor types quickly.
  const seqRef = useRef(0);
  useEffect(() => {
    const seq = ++seqRef.current;
    setPreviewing(true);
    const t = setTimeout(async () => {
      try {
        const data = await api.previewSourceNote(value || "");
        if (seqRef.current !== seq) return;
        setPreview(data?.html || "");
        setPreviewError("");
      } catch (e) {
        if (seqRef.current !== seq) return;
        setPreview("");
        setPreviewError("Preview unavailable.");
      } finally {
        if (seqRef.current === seq) setPreviewing(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        <ToolButton
          onClick={() => wrap("<b>", "</b>")}
          title="Bold the selected text"
          testId="source-note-fmt-bold"
        >
          <Bold className="w-3 h-3" />
        </ToolButton>
        <ToolButton
          onClick={() => wrap("<i>", "</i>")}
          title="Italicise the selected text"
          testId="source-note-fmt-italic"
        >
          <Italic className="w-3 h-3" />
        </ToolButton>
        <ToolButton
          onClick={() => wrap("<u>", "</u>")}
          title="Underline the selected text"
          testId="source-note-fmt-underline"
        >
          <Underline className="w-3 h-3" />
        </ToolButton>
        <ToolButton
          onClick={addLink}
          title="Link the selected text"
          testId="source-note-fmt-link"
        >
          <LinkIcon className="w-3 h-3" />
        </ToolButton>
        <span className="w-px h-4 bg-[var(--border)] mx-1" />
        <ToolButton
          onClick={() => addToken("{source_name}")}
          title="Insert the source-name placeholder"
          testId="source-note-insert-source"
        >
          <span className="font-mono-display">{"{source_name}"}</span>
        </ToolButton>
        <ToolButton
          onClick={() => addToken("{property_name}")}
          title="Insert the property-name placeholder"
          testId="source-note-insert-property"
        >
          <span className="font-mono-display">{"{property_name}"}</span>
        </ToolButton>
      </div>

      <textarea
        ref={taRef}
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        spellCheck={false}
        className="w-full resize-y px-3 py-2 text-xs font-mono-display bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
        placeholder="Leave blank to use Newsdesk's default note."
      />

      <p className="mt-2 text-[11px] text-[var(--ink-muted)]">
        Formatting: <code className="font-mono-display">&lt;b&gt;</code>,{" "}
        <code className="font-mono-display">&lt;i&gt;</code>,{" "}
        <code className="font-mono-display">&lt;u&gt;</code> and links. Any
        other HTML is stripped when the note is added to an article.
      </p>

      <div className="mt-3">
        <div className="overline mb-1.5 flex items-center gap-2">
          Preview
          {previewing && <Loader2 className="w-3 h-3 animate-spin" />}
        </div>
        <div
          data-testid="source-note-preview"
          className="border border-[var(--border)] bg-white px-3 py-2"
        >
          {previewError ? (
            <span className="text-[11px] text-[var(--ink-muted)] italic">
              {previewError}
            </span>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: preview }} />
          )}
        </div>
        <p className="mt-1.5 text-[11px] text-[var(--ink-muted)]">
          Sample sources shown. On a real article every source is listed,
          comma-separated and linked.
        </p>
      </div>

      {defaultTemplate && (value || "") !== defaultTemplate && (
        <button
          type="button"
          data-testid="source-note-template-reset"
          onClick={() => onChange(defaultTemplate)}
          className="mt-2 text-[11px] text-red-600 hover:text-red-800 underline"
        >
          Reset to default note
        </button>
      )}
    </div>
  );
}
